import { useEffect, useState } from "react";
import { Activity, Database, ShieldCheck, Mail, HardDrive, MessageCircle, MessageSquare, CreditCard, Video, RefreshCw } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock } from "@/components/Pagination";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";

const ICONS = {
  API: Activity,
  Database: Database,
  Authentication: ShieldCheck,
  "Email Service (Resend)": Mail,
  "Object Storage": HardDrive,
  "SMS Service": MessageSquare,
  "WhatsApp Service": MessageCircle,
  "Payment Gateway": CreditCard,
  "Video Streaming": Video,
};

function formatUptime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h}h ${m}m ${s}s`;
}

export default function AdminSystemHealthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/admin/system-health");
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <DashboardLayout title="System Health" breadcrumbs={["Platform", "System Health"]}>
        <LoadingBlock label="Checking services…" />
      </DashboardLayout>
    );
  }

  const meta = data.meta;

  return (
    <DashboardLayout title="System Health" breadcrumbs={["Platform", "System Health"]}>
      <div className="space-y-6" data-testid={ADMIN.healthRoot}>
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500">
            Last checked <span className="font-semibold text-slate-700">{new Date(meta.last_check_at).toLocaleTimeString()}</span>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
            data-testid="admin-health-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Recheck
          </button>
        </div>

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.services.map((s) => {
            const Icon = ICONS[s.name] || Activity;
            return (
              <div key={s.name} className="rounded-2xl border border-slate-200 bg-white p-6"
                   data-testid={ADMIN.healthService(s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"))}>
                <div className="flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-50 text-[#101876]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <StatusBadge status={s.status} />
                </div>
                <div className="mt-4 text-sm font-bold text-slate-900">{s.name}</div>
                <div className="mt-1 text-xs text-slate-500">{s.detail}</div>
              </div>
            );
          })}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-sm font-bold text-slate-900">Platform meta</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 text-sm">
            {[
              ["Environment", meta.environment],
              ["Backend version", meta.backend_version],
              ["Frontend version", meta.frontend_version],
              ["Python", meta.python],
              ["Platform", meta.platform],
              ["Uptime", formatUptime(meta.uptime_seconds)],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{k}</div>
                <div className="mt-1 text-sm text-slate-900 break-all">{v}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
